// Éditeur local raccordé aux dépôts existants ; styles et composants de la charte conservés.
import { Bandeau, Bouton, Champ, ConfirmationIntegree, Dialogue, GrilleChamps } from '@cc/ui';
import { Camera } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  captureConfiguree,
  identifiantEspace,
  ouvrirBrouillons,
  type BrouillonsCapture,
} from './brouillons';
import { CoinsCapture } from './CoinsCapture';
import { BrouillonsConserves } from './BrouillonsConserves';
import { PAGE_ENTIERE, quadrilatereValide } from './geometrie';
import { analyserPhoto, produirePhoto } from './traitement';
import type { AnalyseCapture } from './image';
import type { Coins } from './types';
import styles from '../commun/commun.module.css';

export interface ProprietesCapture {
  compteId: string;
  espaceId: string;
  usage: string;
  onFichier: (fichier: File) => Promise<unknown> | void;
  disabled?: boolean;
}

export function CaptureDocument(props: ProprietesCapture) {
  return <CaptureLocale key={`${props.compteId}:${props.espaceId}:${props.usage}`} {...props} />;
}

function CaptureLocale({ compteId, espaceId, usage, onFichier, disabled }: ProprietesCapture) {
  const [ouvert, setOuvert] = useState(false);
  const [espace, setEspace] = useState('');
  const [configure, setConfigure] = useState<boolean | null>(null);
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [deverrouille, setDeverrouille] = useState(false);
  const [fichier, setFichier] = useState<File | null>(null);
  const [analyse, setAnalyse] = useState<AnalyseCapture | null>(null);
  const [coins, setCoins] = useState<Coins>(PAGE_ENTIERE);
  const [apercu, setApercu] = useState<string | null>(null);
  const [produit, setProduit] = useState<Blob | null>(null);
  const [apercuProduit, setApercuProduit] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const brouillons = useRef<BrouillonsCapture | null>(null);
  const traitement = useRef<AbortController | null>(null);
  const vivant = useRef(true);
  const occupe = useRef(false);
  useEffect(() => {
    vivant.current = true;
    return () => {
      vivant.current = false;
      traitement.current?.abort();
      brouillons.current?.fermer();
      brouillons.current = null;
    };
  }, []);
  useEffect(() => {
    if (!analyse) {
      setApercu(null);
      return;
    }
    const url = URL.createObjectURL(analyse.apercu);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [analyse]);
  useEffect(() => {
    if (!produit) {
      setApercuProduit(null);
      return;
    }
    const url = URL.createObjectURL(produit);
    setApercuProduit(url);
    return () => URL.revokeObjectURL(url);
  }, [produit]);
  const executer = async (action: (signal: AbortSignal) => Promise<void>) => {
    if (occupe.current) return;
    occupe.current = true;
    const controle = new AbortController();
    traitement.current = controle;
    setEnCours(true);
    setErreur(null);
    try {
      await action(controle.signal);
    } catch (e) {
      if (vivant.current && !controle.signal.aborted)
        setErreur(e instanceof Error ? e.message : 'Capture impossible');
    } finally {
      occupe.current = false;
      if (vivant.current) setEnCours(false);
    }
  };
  const analyser = async (f: File, signal: AbortSignal, anciens?: Coins) => {
    const r = await analyserPhoto(f, anciens, signal);
    if (!vivant.current || signal.aborted) return;
    setFichier(f);
    setAnalyse(r);
    setCoins(r.coins);
    setProduit(null);
    await brouillons.current!.enregistrer(usage, f, r.coins);
  };
  const ouvrir = () => {
    setOuvert(true);
    void executer(async () => {
      const id = await identifiantEspace(compteId, espaceId);
      const existe = await captureConfiguree(id);
      if (vivant.current) {
        setEspace(id);
        setConfigure(existe);
      }
    });
  };
  const deverrouiller = () =>
    void executer(async (signal) => {
      if (!configure && code !== confirmation)
        throw new Error('Les deux codes doivent être identiques');
      const session = await ouvrirBrouillons(espace, code);
      if (!vivant.current || signal.aborted) {
        session.fermer();
        return;
      }
      brouillons.current = session;
      setDeverrouille(true);
      setCode('');
      setConfirmation('');
      const precedent = await session.lire(usage);
      if (precedent) await analyser(precedent.fichier, signal, precedent.coins);
    });
  const choisir = (f: File) =>
    void executer(async (signal) => {
      // Sauvegarder l'original avant le calcul : une interruption ne le fait pas perdre.
      await brouillons.current!.enregistrer(usage, f, PAGE_ENTIERE);
      setFichier(f);
      setAnalyse(null);
      setProduit(null);
      await analyser(f, signal);
    });
  const preparer = () =>
    void executer(async (signal) => {
      if (!fichier || !quadrilatereValide(coins))
        throw new Error('Les quatre coins doivent former un quadrilatère valide');
      await brouillons.current!.enregistrer(usage, fichier, coins);
      const image = await produirePhoto(fichier, coins, signal);
      if (vivant.current && !signal.aborted) setProduit(image);
    });
  const nettoyer = () => {
    brouillons.current?.fermer();
    brouillons.current = null;
    setDeverrouille(false);
    setFichier(null);
    setAnalyse(null);
    setProduit(null);
    setCode('');
    setConfirmation('');
    setOuvert(false);
    setConfigure(null);
  };
  const fermer = () => {
    if (!occupe.current) nettoyer();
  };
  const utiliser = () =>
    void executer(async () => {
      if (!produit) return;
      await onFichier(new File([produit], 'piece-numerisee.jpg', { type: 'image/jpeg' }));
      // Le brouillon n'est pas effacé par un succès réseau : son effacement est explicite.
      if (vivant.current) nettoyer();
    });
  return (
    <>
      <Bouton
        type="button"
        petit
        variante="secondaire"
        icone={Camera}
        disabled={disabled}
        onClick={ouvrir}
      >
        Numériser une pièce
      </Bouton>
      <Dialogue
        ouvert={ouvert}
        titre="Numériser une pièce"
        onFermer={fermer}
        description="La photo est traitée sur cet appareil. Vérifiez le cadrage puis utilisez la copie dans votre dossier."
        actions={
          <>
            <Bouton type="button" variante="secondaire" disabled={enCours} onClick={fermer}>
              Fermer
            </Bouton>
            {produit ? (
              <Bouton type="button" disabled={enCours} chargement={enCours} onClick={utiliser}>
                Utiliser cette photo
              </Bouton>
            ) : null}
          </>
        }
      >
        <div className={styles.pile}>
          {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
          {!deverrouille ? (
            <>
              <p>
                {configure
                  ? 'Saisissez le code local de vos brouillons de capture.'
                  : 'Choisissez un code local à six chiffres pour protéger les brouillons sur cet appareil.'}{' '}
                Le code oublié ne peut pas être récupéré.
              </p>
              <GrilleChamps>
                <Champ
                  libelle="Code local de capture"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="off"
                />
                {configure === false ? (
                  <Champ
                    libelle="Confirmer le code de capture"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    autoComplete="off"
                  />
                ) : null}
              </GrilleChamps>
              <Bouton
                type="button"
                disabled={enCours || configure === null || !/^\d{6}$/.test(code)}
                chargement={enCours}
                onClick={deverrouiller}
              >
                {configure ? 'Ouvrir mes brouillons' : 'Protéger mes brouillons'}
              </Bouton>
            </>
          ) : (
            <>
              <Champ
                libelle="Photo de la pièce"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                disabled={enCours}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) choisir(f);
                }}
              />
              <p>
                Le brouillon original reste chiffré sur cet appareil après fermeture. Il n’est pas
                envoyé automatiquement.
              </p>
              {brouillons.current ? (
                <BrouillonsConserves session={brouillons.current} usage={usage} />
              ) : null}
              {analyse && apercu ? (
                <>
                  <p>
                    {analyse.detectee
                      ? 'Contour proposé : vérifiez les quatre bords.'
                      : 'Vérifiez les bords ou gardez l’image entière.'}
                  </p>
                  {analyse.qualite.messages.length ? (
                    <Bandeau type="attention">{analyse.qualite.messages.join(' ')}</Bandeau>
                  ) : null}
                  <CoinsCapture
                    coins={coins}
                    apercu={apercu}
                    largeur={analyse.largeur}
                    hauteur={analyse.hauteur}
                    disabled={enCours}
                    onChanger={(c) => {
                      setCoins(c);
                      setProduit(null);
                    }}
                  />
                  {!quadrilatereValide(coins) ? (
                    <Bandeau type="attention">
                      Les coins se croisent ou sont trop proches : ajustez-les avant de continuer.
                    </Bandeau>
                  ) : null}
                  <Bouton
                    type="button"
                    disabled={enCours || !quadrilatereValide(coins)}
                    chargement={enCours}
                    onClick={preparer}
                  >
                    Préparer la photo cadrée
                  </Bouton>
                </>
              ) : null}
              {apercuProduit ? (
                <img
                  src={apercuProduit}
                  alt="Photo cadrée à vérifier avant utilisation"
                  style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                />
              ) : null}
              {fichier ? (
                <ConfirmationIntegree
                  libelle="Effacer le brouillon local"
                  question="Effacer cet original et son cadrage de cet appareil ? Les pièces déjà déposées restent dans le dossier."
                  libelleConfirmer="Effacer ce brouillon"
                  enCours={enCours}
                  onConfirmer={() =>
                    executer(async () => {
                      await brouillons.current!.effacer(usage);
                      setFichier(null);
                      setAnalyse(null);
                      setProduit(null);
                    })
                  }
                />
              ) : null}
            </>
          )}
        </div>
      </Dialogue>
    </>
  );
}
